import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Routes, Route, Navigate } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Container,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Import page components (will be created by other tasks)
import ValidationQueue from './ValidationQueue';
import ValidationWorkers from './ValidationWorkers';
import ValidationBatches from './ValidationBatches';
import ValidationFailureAnalysis from './ValidationFailureAnalysis';
import ValidationActivityLog from './ValidationActivityLog';

// Import overview components (will be created by other tasks)
import StatsCards from '../Components/Validation/StatsCards';
import ProgressBar from '../Components/Validation/ProgressBar';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`validation-tabpanel-${index}`}
      aria-labelledby={`validation-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `validation-tab-${index}`,
    'aria-controls': `validation-tabpanel-${index}`,
  };
}

const StyledTabs = styled((props: any) => (
  <Tabs
    {...props}
    TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
  />
))(({ theme }) => ({
  '& .MuiTabs-indicator': {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  '& .MuiTabs-indicatorSpan': {
    maxWidth: 60,
    width: '100%',
    backgroundColor: theme.palette.primary.main,
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(15),
    minWidth: 120,
  },
}));

export default function ValidationDashboard() {
  const navigate = useNavigate();
  const [value, setValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    const routes = ['', '/queue', '/workers', '/batches', '/failure-analysis', '/activity-log'];
    navigate(`/validation${routes[newValue]}`, { replace: true });
  };

  useEffect(() => {
    // Set initial tab based on route
    const path = window.location.pathname;
    if (path.includes('/queue')) setValue(1);
    else if (path.includes('/workers')) setValue(2);
    else if (path.includes('/batches')) setValue(3);
    else if (path.includes('/failure-analysis')) setValue(4);
    else if (path.includes('/activity-log')) setValue(5);
    else setValue(0);
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Validation Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Monitor and manage validation runs, queue, workers, and analytics
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <StyledTabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="validation dashboard tabs"
        >
          <Tab label="📊 Overview" {...a11yProps(0)} />
          <Tab label="📋 Queue" {...a11yProps(1)} />
          <Tab label="👷 Workers" {...a11yProps(2)} />
          <Tab label="📦 Batches" {...a11yProps(3)} />
          <Tab label="🔍 Failure Analysis" {...a11yProps(4)} />
          <Tab label="📜 Activity Log" {...a11yProps(5)} />
        </StyledTabs>
      </Paper>

      <Paper sx={{ p: 3, minHeight: 600 }}>
        <TabPanel value={value} index={0}>
          <StatsCards />
          <Box sx={{ mt: 4 }}>
            <ProgressBar />
          </Box>
        </TabPanel>
        <TabPanel value={value} index={1}>
          <ValidationQueue />
        </TabPanel>
        <TabPanel value={value} index={2}>
          <ValidationWorkers />
        </TabPanel>
        <TabPanel value={value} index={3}>
          <ValidationBatches />
        </TabPanel>
        <TabPanel value={value} index={4}>
          <ValidationFailureAnalysis />
        </TabPanel>
        <TabPanel value={value} index={5}>
          <ValidationActivityLog />
        </TabPanel>
      </Paper>
    </Container>
  );
}
